import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { GetCards, UpdateCurrentCardDetail } from '@app/finxone-web-frontend/app/lib/actions/card.actions';
import { CardManagementService } from '@app/finxone-web-frontend/app/lib/services/card-management-service/card-management-service';
import { ConfigService } from '@app/finxone-web-frontend/app/lib/services/config-service/config-service.service';
import { ConfirmationService } from '@app/finxone-web-frontend/app/lib/services/confirmation-service/confirmation-service';
import { CardState, CardStateModel } from '@app/finxone-web-frontend/app/lib/state/card.state';
import { RoleState, RoleStateModel } from '@app/finxone-web-frontend/app/lib/state/role.state';
import { setActiveRoleIfNeeded } from '@app/finxone-web-frontend/app/lib/utils/quick-link-page-url.utils';
import {
  combineStyles,
  createRedirectionUrlFromNavigationOptions,
  formatDateMMYY,
  formatPAN,
} from '@app/finxone-web-frontend/app/lib/utils/utils';
import { AutoDestroy } from '@finxone-platform/shared/base-types';
import {
  BaseCssProperties,
  CardAccountType,
  APP_ZONES,
  CardInfoType,
  CardSensitiveDetailsSession,
  CardStatusType,
  CardType,
  CardsWidgetConfig,
  ConfirmationClaimType,
  NavigationOptionEnum,
  NavigationOptionsType,
  ProviderClass,
  SORT_SLUG,
  SecureCardInfoResponse,
  baseCssConfigStyle,
  cardNestedWidgetConfigStyles,
  commonContentStyleList,
  CardTemplateActionType,
} from '@finxone-platform/shared/sys-config-types';
import { Select, Store } from '@ngxs/store';
import {
  Observable,
  Subject,
  catchError,
  distinctUntilChanged,
  filter,
  map,
  switchMap,
  takeUntil,
  throwError,
} from 'rxjs';
import { BaseComponent } from '../../base/base.component';

type CardSensitiveProviderData = {
  equals?: {
    sensitiveDetailsMode?: string;
  };
};
@Component({
  standalone: false,
  selector: 'finxone-web-frontend-card-widget',
  templateUrl: './cards-widget.component.html',
  styleUrl: './cards-widget.component.scss',
})
export class CardsWidgetComponent extends BaseComponent implements OnInit {
  @AutoDestroy destroy$: Subject<void> = new Subject<void>();
  @Select(CardState.getCards)
  private cards!: Observable<CardStateModel>;

  @Select(RoleState.getRole)
  private roles!: Observable<RoleStateModel>;
  roleInfo: RoleStateModel;
  //TODO dummy data to test with, will be removed
  public cardsInfo: CardInfoType[] = [
    {
      id: '123456',
      name: 'John Doe',
      expiry: '01/29',
      maskedPan: '****-****-****-0123',
      type: CardType.VIRTUAL,
      status: CardStatusType.ACTIVE,
      accountType: CardAccountType.TOP_UP,
      createdDate: '01/01/2024',
      activatedDate: '01/01/2024',
    },
    {
      id: '654321',
      name: 'John Doe',
      expiry: '12/35',
      maskedPan: '****-****-****-0987',
      type: CardType.VIRTUAL,
      status: CardStatusType.SUSPENDED,
      accountType: CardAccountType.DEBIT,
      createdDate: '01/08/2024',
      activatedDate: '01/08/2024',
    },
    {
      id: '87654321',
      name: 'John Doe',
      expiry: '4/26',
      maskedPan: '****-****-****-0420',
      type: CardType.PHYSICAL,
      status: CardStatusType.ACTIVE,
      accountType: CardAccountType.TOP_UP,
      createdDate: '11/11/2022',
      activatedDate: '01/08/2024',
    },
    {
      id: '987654321',
      name: 'John Doe',
      expiry: '1/1',
      maskedPan: '****-****-****-0420',
      type: CardType.PHYSICAL,
      accountType: CardAccountType.DEBIT,
      status: CardStatusType.ACTIVE,
      createdDate: '11/11/2022',
      activatedDate: '01/08/2024',
    },
  ];
  public selectedCard: CardInfoType;
  public cardLayoutType: 'carousel' | 'listing' | 'individual';
  public pageSize: number;
  public pagingEnabled: boolean;
  public sortBy: string;
  private doesContextSwitch = true;
  public isWorkflow: boolean;
  private sortKey = 'createdDate';
  private sortOrder: SORT_SLUG = SORT_SLUG.DESC;

  public currentCardDetail: CardInfoType;
  private confirmation: string;
  public secureCVVDetails: SecureCardInfoResponse = { pan: '', cvv2: '', expDate: '' };
  public btnStyle = '';
  public isFlipped: boolean;
  public showBackTemplate = false;
  public flipCardEnabled = true;
  public iconPath = '';
  public isIconAvailable: boolean;
  public iconSize: number;
  public iconStyle: baseCssConfigStyle;
  public iconPlacement?: baseCssConfigStyle;
  public iconPadding: string;
  public backCardTextStyles = {
    label: { 'font-size': '10px', color: 'white' },
    details: { 'font-size': '16px', color: 'white' },
  };
  constructor(
    private configService: ConfigService,
    private store: Store,
    public dataIn: ProviderClass<CardsWidgetConfig>,
    private _router: Router,
    private activatedRoute: ActivatedRoute,
    private cardManagementService: CardManagementService,
    private confirmationService: ConfirmationService,
  ) {
    super();
    this.currentCardDetail = this.store.selectSnapshot(CardState.getCurrentCardDetail);
    this.roles.pipe(takeUntil(this.destroy$)).subscribe((roles) => {
      this.roleInfo = roles;
    });
  }

  ngOnInit() {
    this.isFlipped = this.cardManagementService.isActiveCardFlipped;
    this.handleParamsAndFetchDetails();
    if (this.dataIn != undefined) {
      this.attri = this.dataIn.attri ?? null;
      if (this.attri !== null) {
        this.cardLayoutType = this.attri['cardLayoutType'];
        this.doesContextSwitch = this.attri['doesContextSwitch'] ?? true;
        this.pagingEnabled = this.attri['pagingEnabled'] ?? true;
        this.pageSize = this.attri['pageSize'] ?? 5;
        this.sortBy = this.attri['sortBy'] ?? 'date-added-newest-first';
        this.flipCardEnabled = this.attri['flipCardEnabled'] ?? true;
        this.iconPath = this.attri['iconPath'] ?? 'icon-right';
        this.isIconAvailable = this.attri['isIconAvailable'] ?? false;
        this.iconPlacement = this.getIconPlacementStyle();
        this.iconPadding = `${this.attri['iconPaddingY'] ?? 8}px ${this.attri['iconPaddingX'] ?? 8}px`;
        this.getBackCardTextStyles();
      }
    }
    this.contentStyle = this.configService.getSpecificBaseCssStyle(this.attri, [
      ...commonContentStyleList,
      BaseCssProperties.TEXT_FONT_STYLE,
    ]);
    this.iconStyle = this.configService.getSpecificBaseCssStyle(this.attri, [BaseCssProperties.ICON_SIZE]);
    this.isWorkflow = this.configService.getActiveConfigType().isWorkflow;
    this.getSortParams(this.sortBy);
    this.getCards();
  }

  private getCards() {
    this.store.dispatch(new GetCards(1, this.pageSize, this.sortOrder, this.sortKey));
    this.cards
      .pipe(
        takeUntil(this.destroy$),
        map((cards) => {
          if (cards.items.length) {
            this.cardsInfo = cards.items;
            this.selectedCard = cards.currentCardDetail;
          }
        }),
      )
      .subscribe();
  }

  public indexChanged(index: number) {
    if (this.doesContextSwitch) {
      const cardInfo = this.cardsInfo[index];
      if (cardInfo) {
        this.updateCurrentSelectedCard(cardInfo);
      }
    }
  }

  public handleCardClicked(card?: CardInfoType) {
    if (!card) {
      return;
      // TODO: redirect to add card page?
    }
    if (this.doesContextSwitch) {
      this.updateCurrentSelectedCard(card);
    }
    const cardType = this.getNestedCardType(card);
    const nestedCardWidgetConfig = this.dataIn?.widgetConfig?.nestedWidgets?.[cardType];
    if (nestedCardWidgetConfig) {
      this.goToLink(
        nestedCardWidgetConfig['pageUrl'],
        nestedCardWidgetConfig['role'],
        nestedCardWidgetConfig['zone'],
        nestedCardWidgetConfig['externalUrl'],
        nestedCardWidgetConfig['browserBehaviour'],
      );
    }
    //navigate to page coming in later ticket
  }

  /**
   * A call to action inside a card template (e.g. "Activate Now") navigates with the configuration
   * the app builder set for that action on this card variant, instead of the card's own navigation.
   */
  public handleCardTemplateAction(action: string | CardTemplateActionType, card?: CardInfoType): void {
    if (card && this.doesContextSwitch) {
      this.updateCurrentSelectedCard(card);
    }

    const navigationConfig = this.getCardActionNavigation(card, action);
    if (!navigationConfig || navigationConfig.navigationOption === NavigationOptionEnum.USE_AS_BLANK) {
      return;
    }

    const url = createRedirectionUrlFromNavigationOptions(navigationConfig);
    if (url) {
      this._router.navigateByUrl(url);
    }
  }

  private getCardActionNavigation(
    card: CardInfoType | undefined,
    action: string,
  ): NavigationOptionsType | undefined {
    const cardStylingOptions =
      this.dataIn.widgetConfig?.nestedWidgets?.[this.getNestedCardType(card)]?.widgetProperties?.[
        'cardStylingOptions'
      ];
    return cardStylingOptions?.['actionsNavigation']?.[action];
  }

  public onScroll(event: Event) {
    this.activateWindowScroll(event);
  }

  public activateWindowScroll(event: Event) {
    const element = event.target as HTMLElement;
    if (Math.round(element.scrollHeight - element.scrollTop) === element.clientHeight) {
      window.scrollBy(0, 100);
    }
    if (element.scrollTop === 0) {
      window.scrollBy(0, -100);
    }
  }

  private updateCurrentSelectedCard(card: CardInfoType) {
    this.store.dispatch(new UpdateCurrentCardDetail(card, card.id));
  }

  public getCardTypeStyle(card?: CardInfoType) {
    if (!card) {
      return this.configService.getCardCssStyles(
        this.getNestedWidgetConfig(this.dataIn, this.getNestedCardType()),
        cardNestedWidgetConfigStyles,
      );
    }
    return this.configService.getCardCssStyles(
      this.getNestedWidgetConfig(this.dataIn, this.getNestedCardType(card)),
      cardNestedWidgetConfigStyles,
    );
  }

  public getCardTemplate(card?: CardInfoType) {
    const cardStylingOptions =
      this.dataIn.widgetConfig?.nestedWidgets?.[this.getNestedCardType(card)].widgetProperties[
        'cardStylingOptions'
      ];

    if (card?.status === CardStatusType.PENDING && cardStylingOptions?.['pendingCardTemplate']) {
      return cardStylingOptions?.['pendingCardTemplate'];
    }
    return cardStylingOptions?.['defaultCardTemplate'];
  }

  public getFrozenOverlayTemplate(card?: CardInfoType) {
    return this.dataIn.widgetConfig?.nestedWidgets?.[this.getNestedCardType(card)].widgetProperties[
      'cardStylingOptions'
    ]['frozenCardTemplate'];
  }
  public getFlippedTemplate(card?: CardInfoType) {
    return this.dataIn.widgetConfig?.nestedWidgets?.[this.getNestedCardType(card)].widgetProperties[
      'cardStylingOptions'
    ]['flippedCardTemplate'];
  }

  public getNestedCardType(card?: CardInfoType) {
    if (!card) {
      return 'noCard';
    }
    const cardType = card.type.charAt(0).toUpperCase() + card.type.slice(1);
    return card.accountType === CardAccountType.TOP_UP
      ? `prepaid${cardType}TopUp`
      : `debit${cardType}Current`;
  }

  public goToLink(
    url: string | undefined,
    role: string | undefined,
    zone: string | undefined,
    externalLink?: string | undefined | null,
    browserBehaviour?: string | undefined | null,
  ) {
    if (browserBehaviour && externalLink) {
      if (browserBehaviour === 'new-tab') {
        window.open(externalLink, '_blank');
      } else if (browserBehaviour === 'new-window') {
        window.open(externalLink, '_blank', 'width=800, height=600');
      } else {
        window.open(externalLink, '_self');
      }
      return;
    }
    const isSameRole = this.roleInfo.role === role;

    if ((isSameRole && url) || role === 'no-role') {
      setActiveRoleIfNeeded(zone, this.store);
      this._router.navigate([url]);
    }
  }

  private getSortParams(sortBy: string) {
    switch (sortBy) {
      case 'date-added-newest-first':
        this.sortKey = 'createdDate';
        this.sortOrder = SORT_SLUG.DESC;
        return;
      case 'date-added-oldest-first':
        this.sortKey = 'createdDate';
        this.sortOrder = SORT_SLUG.ASC;
        return;
      case 'physical-first':
        this.sortKey = 'type';
        this.sortOrder = SORT_SLUG.DESC;
        return;
      case 'virtual-first':
        this.sortKey = 'type';
        this.sortOrder = SORT_SLUG.ASC;
        return;
      default:
        break;
    }
  }

  public flipCard() {
    if (!this.supportsInlineSensitiveDetails()) {
      const targetUrl = this.configService.reRouteIfModal(APP_ZONES.CARD_MANAGEMENT_ZONE, 'card-details');
      if (targetUrl) {
        this.confirmationService.confirmAction(
          ConfirmationClaimType.GET_SENSITIVE_CARD_DETAILS,
          targetUrl,
          true,
        );
      }
      return;
    }

    //if card is already flipped showing sensitive details just unflip the card, no need for confirmation
    if (this.cardManagementService.isActiveCardFlipped) {
      this.isFlipped = false;
      this.cardManagementService.isActiveCardFlipped = false;
      //create a delay equal to the flip animation duration to stop the front card template showing whilst the animation is playing
      setTimeout(() => {
        this.showBackTemplate = false;
      }, 600);
      return;
    }
    this.cardManagementService.isActiveCardFlipped = !this.cardManagementService.isActiveCardFlipped;
    this.isFlipped = !this.isFlipped;
    const targetUrl = this._router.url.split('?')[0];
    this.showBackTemplate = true;
    // go auth and then return back to this page
    this.confirmationService.confirmAction(ConfirmationClaimType.GET_SENSITIVE_CARD_DETAILS, targetUrl, true);
  }

  private handleParamsAndFetchDetails(): void {
    if (this.activatedRoute?.queryParams) {
      this.activatedRoute.queryParams
        .pipe(
          takeUntil(this.destroy$),
          distinctUntilChanged(),
          map((paramMap: Params) => {
            this.confirmation = paramMap?.['confirmation'];
            //if user clicks back on the app pin page instead of enterring pin, return to front of card
            if (!this.confirmation) {
              this.isFlipped = false;
              this.cardManagementService.isActiveCardFlipped = false;
            }
            return paramMap;
          }),
          filter(() => Boolean(this.confirmation)),
          switchMap(() => {
            return this.cardManagementService.getSensitiveDetailsSession(
              {
                cardId: this.currentCardDetail.id,
              },
              this.confirmation,
            );
          }),
          map((response: CardSensitiveDetailsSession) => {
            if (response.mode === 'backend-values') {
              this.secureCVVDetails = {
                pan: formatPAN(response.secureDetails?.pan ?? ''),
                cvv2: response.secureDetails?.cvv2 ?? '',
                expDate: response.secureDetails?.expDate || formatDateMMYY(this.currentCardDetail.expiry),
              };
              return;
            }

            this.secureCVVDetails = {
              pan: this.currentCardDetail.maskedPan ?? '',
              cvv2: '***',
              expDate: response.secureDetails?.expDate || formatDateMMYY(this.currentCardDetail.expiry),
            };
          }),
          catchError((error) => {
            console.error('Error fetching CVV details:', error);
            return throwError(() => error);
          }),
        )
        .subscribe();
    }
  }

  //this is needed as when using a percentage to set the width, the percentage needs to be set to both the flip card container and the card itself and will double apply without setting the card to 100%
  public setCardStylesAndFillWidth(card?: CardInfoType): baseCssConfigStyle {
    return combineStyles(this.getCardTypeStyle(card), { width: '100%' });
  }

  private getIconPlacementStyle() {
    const iconPlacement = this.attri['iconPlacement'] ?? 'Top Right';
    let iconPlacementStyle;
    switch (iconPlacement) {
      case 'Top Right':
        iconPlacementStyle = { right: '0px' };
        break;
      case 'Top Left':
        break;
      case 'Bottom Right':
        iconPlacementStyle = { right: '0px', bottom: '0px' };
        break;
      case 'Bottom Left':
        iconPlacementStyle = { bottom: '0px' };
        break;
      default:
        break;
    }

    return iconPlacementStyle;
  }

  private getBackCardTextStyles() {
    this.backCardTextStyles.label['font-size'] = (this.attri['labelFontSize'] ?? '10') + 'px';
    this.backCardTextStyles.details['font-size'] = (this.attri['detailsFontSize'] ?? '16') + 'px';
    this.backCardTextStyles.label.color = this.attri['labelColour'] ?? 'white';
    this.backCardTextStyles.details.color = this.attri['detailsColour'] ?? 'white';
  }

  private supportsInlineSensitiveDetails(): boolean {
    const providerData = (this.currentCardDetail?.providerData ?? {}) as CardSensitiveProviderData;
    return providerData.equals?.sensitiveDetailsMode !== 'provider-sdk';
  }
}
